import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import PageHeaderContainer from 'containers/PageHeader';
import {
  STORES_TYPE,
  ITEMS_TYPE,
  ACCOUNTS_TYPE,
  CONTEXT_STORE_GROUP,
  GlobalFilterKey
} from 'utils/types';
import { periodEndWeekSelector } from 'containers/AccessControl/selectors';
import * as globalSelectors from 'store/global/selectors';
import SalesHighlightsWidget from 'components/SalesHighlightsWidget';
import * as pageHeaderActions from 'containers/PageHeader/actions';
import { detailsFilterSelector } from 'containers/PageHeader/selectors';
import QueryRetryWrapper from 'components/QueryRetryWrapper';
import { GET_GROUP_INFO } from 'apollo/queries';
import { favGroupsClient } from 'apollo';
import FilterBarContainer from 'containers/PageHeader/FilterBar';
import { OTHER_ROUTES } from 'utils/routes';
import ReactGA from 'react-ga';

import {
  QueryResult,
  QueryStateResult,
  useTableQuery,
  useTableQueryState
} from '@spins/react-table';
import { ACCOUNTS_DETAIL_SERIES, ITEMS_DETAIL_SERIES, STORES_DETAIL_SERIES } from 'apollo/queries';
import StoresTable from 'components/Tables/Stores';
import ItemsTable from 'components/Tables/Items';
import AccountsTable from 'components/Tables/Accounts';
import TableTabs from 'components/TableTabs';
import { SearchInput, Widget } from '@spins/react-ui-library';
import DistributionChartWidget from 'components/Charts/Widgets/DistributionChartWidget';
import TrendChartWidget from 'components/Charts/Widgets/TrendChartWidget';
import TableFilterTiles from 'components/Filter/TableFilterTiles';

const GroupDetails = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const dispatch = useDispatch();
  const clientId: string = useSelector(globalSelectors.clientIdSelector);
  const currentBrand: string = useSelector(globalSelectors.currentBrandSelector);
  const globalFilter = useSelector(detailsFilterSelector);
  const periodEndWeek = useSelector(periodEndWeekSelector);
  const [selectedTab, setSelectedTab] = useState('');
  const [typesPreloaded, setTypesPreloaded] = useState({
    accounts: false,
    items: false,
    states: false,
    stores: false
  });

  useEffect(() => {
    ReactGA.pageview(OTHER_ROUTES.GROUP_DETAILS);
    return () => {
      dispatch(pageHeaderActions.clearGlobalFilter(GlobalFilterKey.DETAILS));
    };
  }, [dispatch]);

  // tables state
  const tablesState: QueryStateResult = useTableQueryState([
    {
      id: 'accounts',
      defaultSortAttribute: 'sales',
      queryVariables: {
        clientID: clientId,
        globalFilter: globalFilter
      }
    },
    {
      id: 'stores',
      defaultSortAttribute: 'sales',
      queryVariables: {
        clientID: clientId,
        globalFilter: globalFilter
      }
    },
    {
      id: 'items',
      defaultSortAttribute: 'sales',
      queryVariables: {
        clientID: clientId,
        globalFilter: globalFilter
      }
    }
  ]);

  // accounts query
  const {
    data: accountsData,
    loading: accountsLoading,
    error: accountsError
  }: QueryResult = useTableQuery(ACCOUNTS_DETAIL_SERIES, {
    fetchPolicy: 'cache-first',
    queryState: tablesState.queryState('accounts'),
    searchAttributes: ['name'],
    skip: params.has('account')
  });

  // stores query
  const {
    data: storesData,
    loading: storesLoading,
    error: storesError
  }: QueryResult = useTableQuery(STORES_DETAIL_SERIES, {
    fetchPolicy: 'cache-first',
    queryState: tablesState.queryState('stores'),
    searchAttributes: ['name'],
    skip: params.has('store')
  });

  // items query
  const { data: itemsData, loading: itemsLoading, error: itemsError }: QueryResult = useTableQuery(
    ITEMS_DETAIL_SERIES,
    {
      fetchPolicy: 'cache-first',
      queryState: tablesState.queryState('items'),
      searchAttributes: ['name'],
      skip: params.has('item')
    }
  );

  const getAvailableTabs = (context: string) => {
    if (context === CONTEXT_STORE_GROUP) {
      return [
        {
          name: STORES_TYPE,
          total:
            storesData && storesData.storeDetailsSeries
              ? storesData.storeDetailsSeries.page.totalItems
              : 0
        },
        {
          name: ACCOUNTS_TYPE,
          total:
            accountsData && accountsData.accountDetailsSeries
              ? accountsData.accountDetailsSeries.page.totalItems
              : 0
        },
        {
          name: ITEMS_TYPE,
          total:
            itemsData && itemsData.itemsDetailsSeries
              ? itemsData.itemsDetailsSeries.page.totalItems
              : 0
        }
      ];
    }
    return [
      {
        name: ITEMS_TYPE,
        total:
          itemsData && itemsData.itemsDetailsSeries
            ? itemsData.itemsDetailsSeries.page.totalItems
            : 0
      },
      {
        name: ACCOUNTS_TYPE,
        total:
          accountsData && accountsData.accountDetailsSeries
            ? accountsData.accountDetailsSeries.page.totalItems
            : 0
      },
      {
        name: STORES_TYPE,
        total:
          storesData && storesData.storeDetailsSeries
            ? storesData.storeDetailsSeries.page.totalItems
            : 0
      }
    ];
  };

  return (
    /**
     * Avoid using cache on this query otherwise the onCompleted will not be executed
     * and the data will not be loaded into redux
     */
    <QueryRetryWrapper
      client={favGroupsClient as any}
      fetchPolicy="network-only"
      query={GET_GROUP_INFO}
      variables={{
        id: params.get('group_id')
      }}
      onCompleted={data => {
        const groupContext = data.fetchFilterGroup.context;
        if (groupContext === CONTEXT_STORE_GROUP) {
          setSelectedTab(STORES_TYPE);
        } else {
          setSelectedTab(ITEMS_TYPE);
        }
        const { accounts, items, states, stores } = data.fetchFilterGroup.data;
        setTypesPreloaded({
          accounts: accounts && accounts.length > 0,
          items: items && items.length > 0,
          states: states && states.length > 0,
          stores: stores && stores.length > 0
        });
        dispatch(
          pageHeaderActions.updateGlobalFilterArrays(
            {
              accounts: accounts ? accounts : [],
              items: items ? items : [],
              states: states ? states : [],
              stores: stores ? stores : []
            },
            GlobalFilterKey.DETAILS
          )
        );
      }}
    >
      {({ data }) => {
        const groupContext = data.fetchFilterGroup.context;
        return (
          <React.Fragment>
            <Row>
              <PageHeaderContainer
                globalFilterContext={GlobalFilterKey.DETAILS}
                template={groupContext === CONTEXT_STORE_GROUP ? 'storeGroups' : 'itemGroups'}
                typesPreloaded={typesPreloaded}
                onSelectedTabChange={key => console.log(`set tab ${key}`)}
                exportPdfAvailable={true}
              />
            </Row>
            <FilterBarContainer
              globalFilterContext={GlobalFilterKey.DETAILS}
              typesPreloaded={typesPreloaded}
            />
            <Row className="mt-3">
              <Col>
                <SalesHighlightsWidget
                  clientId={clientId}
                  periodEndWeek={periodEndWeek}
                  currentBrand={currentBrand}
                  globalFilter={globalFilter}
                />
              </Col>
            </Row>
            <Row>
              <Col className="mt-3" lg={12} xl={6}>
                <TrendChartWidget globalFilter={globalFilter} />
              </Col>
              <Col className="mt-3" lg={12} xl={6}>
                <DistributionChartWidget globalFilter={globalFilter} />
              </Col>
            </Row>
            {tablesState.sharedState.filtering.filters.length > 0 && (
              <Row className="mt-3">
                <Col>
                  <TableFilterTiles filtering={tablesState.sharedState.filtering} />
                </Col>
              </Row>
            )}
            <Row className="mt-3">
              <Col>
                <Widget>
                  {{
                    header: {
                      content: {
                        left: (
                          <TableTabs
                            selectedTab={selectedTab}
                            tabs={getAvailableTabs(groupContext)}
                            onSelect={key => {
                              setSelectedTab(key);
                            }}
                          />
                        ),
                        center: (
                          <SearchInput
                            style={{ width: '300px' }}
                            placeholder="Search..."
                            onChange={value => {
                              tablesState.sharedState.searching.search(value);
                            }}
                          />
                        )
                      }
                    },
                    body: {
                      content: (
                        <>
                          {selectedTab === ACCOUNTS_TYPE && (
                            <AccountsTable
                              error={accountsError}
                              queryState={tablesState.queryState('accounts')}
                              rows={
                                accountsData && accountsData.accountDetailsSeries
                                  ? accountsData.accountDetailsSeries.data
                                  : []
                              }
                              showLoading={accountsLoading}
                              totalRows={
                                accountsData && accountsData.accountDetailsSeries
                                  ? accountsData.accountDetailsSeries.page.totalItems
                                  : 0
                              }
                            />
                          )}
                          {selectedTab === STORES_TYPE && (
                            <StoresTable
                              error={storesError}
                              queryState={tablesState.queryState('stores')}
                              rows={
                                storesData && storesData.storeDetailsSeries
                                  ? storesData.storeDetailsSeries.data
                                  : []
                              }
                              showTopOpportunity={false}
                              showLoading={storesLoading}
                              totalRows={
                                storesData && storesData.storeDetailsSeries
                                  ? storesData.storeDetailsSeries.page.totalItems
                                  : 0
                              }
                            />
                          )}
                          {selectedTab === ITEMS_TYPE && (
                            <ItemsTable
                              error={itemsError}
                              queryState={tablesState.queryState('items')}
                              rows={
                                itemsData && itemsData.itemsDetailsSeries
                                  ? itemsData.itemsDetailsSeries.data
                                  : []
                              }
                              showLoading={itemsLoading}
                              totalRows={
                                itemsData && itemsData.itemsDetailsSeries
                                  ? itemsData.itemsDetailsSeries.page.totalItems
                                  : 0
                              }
                            />
                          )}
                        </>
                      )
                    }
                  }}
                </Widget>
              </Col>
            </Row>
          </React.Fragment>
        );
      }}
    </QueryRetryWrapper>
  );
};

export default GroupDetails;
